// client/src/components/pages/RouteSummary/RegNoTransactionsModal.tsx (converted from .js, ST-00216)
//
// Every ticket of one truck on the route(s) being reconciled, opened from a Reg
// No in the Recon view. ST-00218 (lm-status): with load matching on, each ticket's
// "Load status" (the tenant's load-match ledger) follows Ticket No.
import { useState, useEffect, useMemo, type MouseEvent } from 'react';
import { FaTimes, FaSpinner } from 'react-icons/fa';
import api from '../../../services/api';
import { MINING_TZ } from '../../../utils/dateUtils';
import { groupingScopeParams } from './routeSummaryParams';
import { useLoadStatuses } from '../../../hooks/useLoadStatuses';
import { LoadStatusCell } from '../../common/LoadStatusBadge';
import type { GroupingId } from '../../../types/clientPages/routeSummary';
import type {
  RegNoTransaction,
  RegNoTransactionsResponse,
  RouteSummaryApiError,
} from '../../../types/clientPages/routeSummaryRecon';

export interface RegNoTransactionsModalProps {
  isOpen: boolean;
  onClose: () => void;
  regNo: string | null;
  groupingId: GroupingId;
  groupingIds?: GroupingId[] | null;
  startDate: string;
  endDate: string;
  minWeight: number;
  clickedTicketNo: string | null;
}

const RegNoTransactionsModal = ({
  isOpen,
  onClose,
  regNo,
  groupingId,
  // ST-00215: every route of a combined summary. The list is the truck's
  // tickets on the route(s) being reconciled (the server also limits it to
  // routes your organisation can see).
  groupingIds = null,
  startDate,
  endDate,
  minWeight,
  clickedTicketNo,
}: RegNoTransactionsModalProps) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [transactions, setTransactions] = useState<RegNoTransaction[]>([]);

  const scopeKey = (groupingIds || []).join(',');
  useEffect(() => {
    if (isOpen && regNo && groupingId) {
      fetchTransactions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, regNo, groupingId, scopeKey, startDate, endDate, minWeight]);

  const loadStatusUids = useMemo(() => transactions.map((t) => t.ticketUID), [transactions]);
  const {
    enabled: loadStatusEnabled,
    statuses: loadStatuses,
    loading: loadStatusLoading,
  } = useLoadStatuses(loadStatusUids, { skip: !isOpen });
  const showLoadStatus = loadStatusEnabled === true;

  const fetchTransactions = async () => {
    setLoading(true);
    setError(null);

    try {
      console.log('Fetching transactions for Reg No:', {
        groupingId,
        regNo,
        startDate,
        endDate,
        minWeight,
      });

      // Only called with a regNo (the effect checks it; Retry shows after a fetch).
      const response = await api.routeSummary.getTransactionsByRegNo(groupingId, regNo as string, {
        startDate,
        endDate,
        minWeight,
        ...groupingScopeParams(groupingIds),
      });
      const data = response.data as RegNoTransactionsResponse;

      console.log('Transactions response:', data);
      setTransactions(data.transactions || []);
    } catch (caught) {
      const err = caught as RouteSummaryApiError;
      console.error('Error fetching transactions by Reg No:', err);
      console.error('Error details:', {
        status: err.response?.status,
        statusText: err.response?.statusText,
        data: err.response?.data,
        message: err.message,
      });

      const errorMessage =
        err.response?.data?.error ||
        err.response?.data?.message ||
        err.message ||
        'Failed to load transactions';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      timeZone: MINING_TZ,
    });
  };

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      timeZone: MINING_TZ,
    });
  };

  if (!isOpen) return null;

  // Handler for backdrop click
  const handleBackdropClick = (e: MouseEvent<HTMLDivElement>) => {
    // Only close if clicking on the backdrop itself, not the modal content
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4"
      onClick={handleBackdropClick}
    >
      <div className="bg-surface rounded-2xl shadow-2xl max-w-6xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 bg-gradient-to-r from-slate-800 to-slate-700 dark:from-slate-100 dark:to-slate-200 text-white rounded-t-xl flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold">Transactions for {regNo}</h2>
            <p className="text-sm text-slate-300 mt-1">
              {startDate} to {endDate} • {transactions.length} transaction
              {transactions.length !== 1 ? 's' : ''}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:text-slate-300 transition-colors p-2"
          >
            <FaTimes className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto">
          {loading && (
            <div className="flex items-center justify-center h-64 p-6">
              <div className="text-center">
                <FaSpinner className="animate-spin w-8 h-8 text-primary-500 mx-auto mb-4" />
                <p className="text-gray-600">Loading transactions...</p>
              </div>
            </div>
          )}

          {error && !loading && (
            <div className="flex items-center justify-center h-64 p-6">
              <div className="text-center">
                <p className="text-red-600 mb-4">{error}</p>
                <button
                  onClick={fetchTransactions}
                  className="px-4 py-2 bg-primary-500 text-white rounded-md hover:bg-primary-600 transition-colors"
                >
                  Retry
                </button>
              </div>
            </div>
          )}

          {!loading && !error && transactions.length === 0 && (
            <div className="flex items-center justify-center h-64 p-6">
              <p className="text-gray-600">No transactions found for this registration number.</p>
            </div>
          )}

          {!loading && !error && transactions.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead className="bg-slate-900 dark:bg-slate-50 sticky top-0 z-10">
                  <tr>
                    <th
                      className="px-4 py-3 text-xs font-bold text-white uppercase tracking-wider border-r border-slate-700 dark:border-slate-200 text-center"
                      style={{ whiteSpace: 'nowrap' }}
                    >
                      #
                    </th>
                    <th
                      className="px-4 py-3 text-xs font-bold text-white uppercase tracking-wider border-r border-slate-700 dark:border-slate-200 text-left"
                      style={{ whiteSpace: 'nowrap' }}
                    >
                      Date
                    </th>
                    <th
                      className="px-4 py-3 text-xs font-bold text-white uppercase tracking-wider border-r border-slate-700 dark:border-slate-200 text-left"
                      style={{ whiteSpace: 'nowrap' }}
                    >
                      Ticket Timestamp
                    </th>
                    <th
                      className="px-4 py-3 text-xs font-bold text-white uppercase tracking-wider border-r border-slate-700 dark:border-slate-200 text-left"
                      style={{ whiteSpace: 'nowrap' }}
                    >
                      Ticket Type
                    </th>
                    <th
                      className="px-4 py-3 text-xs font-bold text-white uppercase tracking-wider border-r border-slate-700 dark:border-slate-200 text-left"
                      style={{ whiteSpace: 'nowrap' }}
                    >
                      Ticket No
                    </th>
                    {showLoadStatus && (
                      <th
                        className="px-4 py-3 text-xs font-bold text-white uppercase tracking-wider border-r border-slate-700 dark:border-slate-200 text-left"
                        style={{ whiteSpace: 'nowrap' }}
                      >
                        Load status
                      </th>
                    )}
                    <th
                      className="px-4 py-3 text-xs font-bold text-white uppercase tracking-wider border-r border-slate-700 dark:border-slate-200 text-left"
                      style={{ whiteSpace: 'nowrap' }}
                    >
                      Delivery No
                    </th>
                    <th
                      className="px-4 py-3 text-xs font-bold text-white uppercase tracking-wider border-r border-slate-700 dark:border-slate-200 text-left"
                      style={{ whiteSpace: 'nowrap' }}
                    >
                      Dispatch Location
                    </th>
                    <th
                      className="px-4 py-3 text-xs font-bold text-white uppercase tracking-wider border-r border-slate-700 dark:border-slate-200 text-left"
                      style={{ whiteSpace: 'nowrap' }}
                    >
                      Receipt Location
                    </th>
                    <th
                      className="px-4 py-3 text-xs font-bold text-white uppercase tracking-wider border-r border-slate-700 dark:border-slate-200 text-right"
                      style={{ whiteSpace: 'nowrap' }}
                    >
                      Nett Weight
                    </th>
                    <th
                      className="px-4 py-3 text-xs font-bold text-white uppercase tracking-wider text-left"
                      style={{ whiteSpace: 'nowrap' }}
                    >
                      Product
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-surface">
                  {transactions.map((transaction, index) => {
                    const isHighlighted = transaction.ticketNo === clickedTicketNo;
                    return (
                      <tr
                        key={`${transaction.ticketNo}-${index}`}
                        className={`border-b border-slate-200 transition-colors ${
                          isHighlighted
                            ? 'bg-yellow-100 border-yellow-400 border-2'
                            : transaction.ticketType === 'Receipt'
                              ? 'bg-emerald-50 hover:bg-emerald-100'
                              : 'bg-surface hover:bg-slate-100'
                        }`}
                        style={{ height: '2rem' }}
                      >
                        <td className="px-3 py-2 text-sm text-slate-600 border-r border-slate-200 text-center font-medium">
                          {index + 1}
                        </td>
                        <td
                          className="px-3 py-2 text-sm text-slate-900 border-r border-slate-200"
                          style={{ whiteSpace: 'nowrap' }}
                        >
                          {formatDate(transaction.ticketTimestamp)}
                        </td>
                        <td
                          className="px-3 py-2 text-sm text-slate-900 border-r border-slate-200"
                          style={{ whiteSpace: 'nowrap' }}
                        >
                          {formatDateTime(transaction.ticketTimestamp)}
                        </td>
                        <td
                          className="px-3 py-2 text-sm text-slate-900 border-r border-slate-200"
                          style={{ whiteSpace: 'nowrap' }}
                        >
                          {transaction.ticketType}
                        </td>
                        <td
                          className="px-3 py-2 text-sm text-slate-900 border-r border-slate-200"
                          style={{ whiteSpace: 'nowrap' }}
                        >
                          {transaction.ticketNo}
                        </td>
                        {showLoadStatus && (
                          <td
                            className="px-3 py-2 text-sm text-slate-900 border-r border-slate-200"
                            style={{ whiteSpace: 'nowrap' }}
                          >
                            <LoadStatusCell
                              status={
                                transaction.ticketUID ? loadStatuses[transaction.ticketUID] : null
                              }
                              loading={loadStatusLoading}
                            />
                          </td>
                        )}
                        <td
                          className="px-3 py-2 text-sm text-slate-900 border-r border-slate-200"
                          style={{ whiteSpace: 'nowrap' }}
                        >
                          {transaction.deliveryNo || '-'}
                        </td>
                        <td
                          className="px-3 py-2 text-sm text-slate-900 border-r border-slate-200"
                          style={{ whiteSpace: 'nowrap' }}
                        >
                          {transaction.dispatchLocation}
                        </td>
                        <td
                          className="px-3 py-2 text-sm text-slate-900 border-r border-slate-200"
                          style={{ whiteSpace: 'nowrap' }}
                        >
                          {transaction.receiptLocation}
                        </td>
                        <td
                          className="px-3 py-2 text-sm text-slate-900 border-r border-slate-200 text-right font-semibold"
                          style={{ whiteSpace: 'nowrap' }}
                        >
                          {transaction.nettWeight?.toLocaleString(undefined, {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          }) || '0.00'}
                        </td>
                        <td
                          className="px-3 py-2 text-sm text-slate-900"
                          style={{ whiteSpace: 'nowrap' }}
                        >
                          {transaction.product}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-slate-50 border-t border-slate-200 rounded-b-xl flex justify-end">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-slate-700 dark:bg-slate-200 text-white rounded-lg hover:bg-slate-800 dark:hover:bg-slate-100 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default RegNoTransactionsModal;
